import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Settings, Building2, Bell, Users, Wallet, Webhook, Shield, FileText, DollarSign, Mail, Flag, Palette } from 'lucide-react';
import { AdminSettings } from './admin/AdminSettings';
import { AdminAccount } from './admin/AdminAccount';
import { AdminNotifications } from './admin/AdminNotifications';
import { UserManagement } from './UserManagement';
import { AccountsManagement } from './admin/AccountsManagement';
import { WebhookLogs } from './admin/WebhookLogs';
import RoleManagement from './admin/RoleManagement';
import { AuditLogs } from './admin/AuditLogs';
import { SubscriptionPlanConfig } from './admin/SubscriptionPlanConfig';
import { EDMManager } from './admin/EDMManager';
import { ExtractorManager } from './admin/ExtractorManager';
import { FeatureFlags } from './admin/FeatureFlags';
import { BrandingConfig } from './admin/BrandingConfig';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionsContext';
import { useTranslation } from '../i18n/hooks/useTranslation';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import { useNavigation } from '../contexts/NavigationContext';

type AdminTab = 'settings' | 'profile' | 'account' | 'notifications' | 'accounts' | 'users' | 'roles' | 'webhooks' | 'dashboard' | 'audit' | 'extractor' | 'plan-config' | 'edm' | 'feature-flags' | 'branding';

// Map AdminTab to URL path segment
const tabToPath: Record<AdminTab, string> = {
  'settings': 'settings',
  'profile': 'profile',
  'account': 'account',
  'notifications': 'notifications',
  'accounts': 'accounts',
  'users': 'users',
  'roles': 'roles',
  'webhooks': 'webhooks',
  'dashboard': 'dashboard',
  'audit': 'audit',
  'extractor': 'extractor',
  'plan-config': 'subscriptions',
  'edm': 'edm',
  'feature-flags': 'feature-flags',
  'branding': 'branding'
};

// Map URL path segment to AdminTab
const pathToTab: Record<string, AdminTab> = {
  'settings': 'settings',
  'profile': 'profile',
  'account': 'account',
  'notifications': 'notifications',
  'accounts': 'accounts',
  'users': 'users',
  'roles': 'roles',
  'webhooks': 'webhooks',
  'dashboard': 'dashboard',
  'audit': 'audit',
  'extractor': 'extractor',
  'subscriptions': 'plan-config',
  'edm': 'edm',
  'feature-flags': 'feature-flags',
  'branding': 'branding'
};

export const Admin: React.FC = () => {
  const { user } = useAuth();
  const { hasPermission } = usePermissions();
  const { t } = useTranslation();
  const { setCustomBreadcrumbs } = useBreadcrumbs();
  const { currentPath, navigate } = useNavigation();
  const isSystemAdmin = user?.role === 'System Administrator';
  const hasAdminPermission = hasPermission('Admin', 'view');

  // Read tab from URL path or query parameter (for backward compatibility)
  const getTabFromUrl = useCallback((): AdminTab | null => {
    // Use currentPath from NavigationContext, fallback to window.location for initial render
    const pathToCheck = currentPath || (window.location.pathname + window.location.search);
    const [pathname, queryString] = pathToCheck.split('?');
    const pathSegments = pathname.split('/').filter(Boolean);
    
    // Check if we have a path-based route like /admin/subscriptions
    if (pathSegments.length >= 2 && pathSegments[0] === 'admin') {
      const pathSegment = pathSegments[1];
      if (pathToTab[pathSegment]) {
        return pathToTab[pathSegment];
      }
    }
    
    // Fallback to query parameter for backward compatibility
    if (queryString) {
      const urlParams = new URLSearchParams(queryString);
      const tabFromQuery = urlParams.get('tab') as AdminTab;
      if (tabFromQuery && pathToTab[tabFromQuery] !== undefined) {
        return tabFromQuery;
      }
    }
    
    return null;
  }, [currentPath]);

  // System Admins default to 'plan-config' (Subscriptions), Account Owners/users with Admin permission default to 'settings', others to 'profile'
  const defaultTab = isSystemAdmin ? 'plan-config' : (hasAdminPermission ? 'settings' : 'profile');
  const [activeTab, setActiveTab] = useState<AdminTab>(getTabFromUrl() || defaultTab);

  // Redirect System Admins from /admin to /admin/subscriptions if no sub-path
  useEffect(() => {
    if (isSystemAdmin) {
      const pathname = currentPath.split('?')[0];
      const pathSegments = pathname.split('/').filter(Boolean);
      
      // If at /admin with no sub-path, redirect to /admin/subscriptions
      if (pathname === '/admin' || (pathSegments.length === 1 && pathSegments[0] === 'admin')) {
        const urlParams = new URLSearchParams(currentPath.split('?')[1] || '');
        // Only redirect if there's no tab query parameter (which would be legacy)
        if (!urlParams.get('tab')) {
          navigate('/admin/subscriptions');
        }
      }
    }
  }, [isSystemAdmin, currentPath, navigate]);

  // Sync activeTab when URL pathname changes (for browser back/forward and navigation)
  useEffect(() => {
    const tabFromUrl = getTabFromUrl();
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [currentPath, getTabFromUrl]);

  // Update URL when tab changes - use path-based routing for System Admins, query params for others
  useEffect(() => {
    const pathSegment = tabToPath[activeTab];
    if (isSystemAdmin) {
      // System Admins use path-based routing: /admin/subscriptions
      const newPath = `/admin/${pathSegment}`;
      window.history.replaceState({}, '', newPath);
    } else {
      // Non-System Admins use query parameters for backward compatibility: /admin?tab=settings
      const url = new URL(window.location.href);
      url.searchParams.set('tab', activeTab);
      window.history.replaceState({}, '', url.toString());
    }
  }, [activeTab, isSystemAdmin]);

  // Track current tab to prevent stale updates
  const currentTabRef = useRef<AdminTab>(activeTab);
  currentTabRef.current = activeTab;
  
  // Update breadcrumbs when tab changes - use useLayoutEffect for synchronous updates
  useLayoutEffect(() => {
    const tab = currentTabRef.current;
    
    // Don't set breadcrumbs for 'settings' or 'account' - let their components handle it
    if (tab === 'settings' || tab === 'account') {
      setCustomBreadcrumbs(null);
      return;
    }
    
    // Helper to get path based on user role
    const getAdminPath = (tab: AdminTab): string => {
      const pathSegment = tabToPath[tab];
      return isSystemAdmin ? `/admin/${pathSegment}` : `/admin?tab=${tab}`;
    };

    const breadcrumbMap: Record<Exclude<AdminTab, 'settings' | 'account'>, Array<{ label: string; path: string; icon: string }>> = {
      'profile': [
        { label: t('navigation.home'), path: '/', icon: 'Home' },
        { label: t('navigation.admin'), path: getAdminPath('profile'), icon: 'Settings' },
        { label: t('navigation.profile'), path: getAdminPath('profile'), icon: 'User' }
      ],
      'notifications': [
        { label: t('navigation.home'), path: '/', icon: 'Home' },
        { label: t('navigation.admin'), path: getAdminPath('notifications'), icon: 'Settings' },
        { label: t('admin.notifications'), path: getAdminPath('notifications'), icon: 'Bell' }
      ],
      'accounts': [
        { label: t('navigation.home'), path: '/', icon: 'Home' },
        { label: t('navigation.admin'), path: getAdminPath('accounts'), icon: 'Settings' },
        { label: t('admin.accounts'), path: getAdminPath('accounts'), icon: 'Wallet' }
      ],
      'users': [
        { label: t('navigation.home'), path: '/', icon: 'Home' },
        { label: t('navigation.admin'), path: getAdminPath('users'), icon: 'Settings' },
        { label: t('admin.users'), path: getAdminPath('users'), icon: 'Users' }
      ],
      'roles': [
        { label: t('navigation.home'), path: '/', icon: 'Home' },
        { label: t('navigation.admin'), path: getAdminPath('roles'), icon: 'Settings' },
        { label: t('admin.roles'), path: getAdminPath('roles'), icon: 'Shield' }
      ],
      'dashboard': [
        { label: t('navigation.home'), path: '/', icon: 'Home' },
        { label: t('navigation.admin'), path: getAdminPath('dashboard'), icon: 'Settings' },
        { label: t('admin.experience'), path: getAdminPath('dashboard'), icon: 'LayoutDashboard' }
      ],
      'audit': [
        { label: t('navigation.home'), path: '/', icon: 'Home' },
        { label: t('navigation.admin'), path: getAdminPath('audit'), icon: 'Settings' },
        { label: t('admin.audit'), path: getAdminPath('audit'), icon: 'FileText' }
      ],
      'extractor': [
        { label: t('navigation.home'), path: '/', icon: 'Home' },
        { label: t('navigation.admin'), path: getAdminPath('extractor'), icon: 'Settings' },
        { label: 'Extractor', path: getAdminPath('extractor'), icon: 'FileText' }
      ],
      'webhooks': [
        { label: t('navigation.home'), path: '/', icon: 'Home' },
        { label: t('navigation.admin'), path: getAdminPath('webhooks'), icon: 'Settings' },
        { label: t('admin.webhooks'), path: getAdminPath('webhooks'), icon: 'Webhook' }
      ],
      'plan-config': [
        { label: t('navigation.home'), path: '/', icon: 'Home' },
        { label: t('navigation.admin'), path: getAdminPath('plan-config'), icon: 'Settings' },
        { label: t('admin.subscriptions'), path: getAdminPath('plan-config'), icon: 'DollarSign' }
      ],
      'edm': [
        { label: t('navigation.home'), path: '/', icon: 'Home' },
        { label: t('navigation.admin'), path: getAdminPath('edm'), icon: 'Settings' },
        { label: t('edm.title') || 'Marketing', path: getAdminPath('edm'), icon: 'Mail' }
      ],
      'feature-flags': [
        { label: t('navigation.home'), path: '/', icon: 'Home' },
        { label: t('navigation.admin'), path: getAdminPath('feature-flags'), icon: 'Settings' },
        { label: 'Feature Flags', path: getAdminPath('feature-flags'), icon: 'Flag' }
      ],
      'branding': [
        { label: t('navigation.home'), path: '/', icon: 'Home' },
        { label: t('navigation.admin'), path: getAdminPath('branding'), icon: 'Settings' },
        { label: 'Branding', path: getAdminPath('branding'), icon: 'Palette' }
      ]
    };

    const breadcrumbs = breadcrumbMap[tab as Exclude<AdminTab, 'settings' | 'account'>];
    if (breadcrumbs && currentTabRef.current === tab) {
      // Only set if we're still on the same tab (prevent race conditions)
      setCustomBreadcrumbs(breadcrumbs);
    }
    
    // Cleanup: clear breadcrumbs if tab changed
    return () => {
      if (currentTabRef.current !== tab) {
        setCustomBreadcrumbs(null);
      }
    };
  }, [activeTab, t, setCustomBreadcrumbs, isSystemAdmin]);

  const allTabs = [
    { id: 'settings' as AdminTab, label: t('navigation.settings'), icon: Settings, hideForSystemAdmin: true, showOnlyForSystemAdmin: false, requiresAdminPermission: true },
    { id: 'account' as AdminTab, label: t('navigation.account'), icon: Building2, hideForSystemAdmin: true, showOnlyForSystemAdmin: false, requiresAdminPermission: true },
    { id: 'notifications' as AdminTab, label: t('admin.notifications'), icon: Bell, hideForSystemAdmin: true, showOnlyForSystemAdmin: false, requiresAdminPermission: true },
    { id: 'accounts' as AdminTab, label: t('admin.accounts'), icon: Wallet, hideForSystemAdmin: false, showOnlyForSystemAdmin: true, requiresAdminPermission: false },
    { id: 'users' as AdminTab, label: t('admin.users'), icon: Users, hideForSystemAdmin: false, showOnlyForSystemAdmin: false, requiresAdminPermission: true },
    { id: 'roles' as AdminTab, label: t('admin.roles'), icon: Shield, hideForSystemAdmin: true, showOnlyForSystemAdmin: false, requiresAdminPermission: true },
    { id: 'audit' as AdminTab, label: t('admin.audit'), icon: FileText, hideForSystemAdmin: false, showOnlyForSystemAdmin: false, requiresAdminPermission: false, requiresAuditPermission: true },
    { id: 'extractor' as AdminTab, label: 'Extractor', icon: FileText, hideForSystemAdmin: false, showOnlyForSystemAdmin: false, requiresAdminPermission: true },
    { id: 'webhooks' as AdminTab, label: t('admin.webhooks'), icon: Webhook, hideForSystemAdmin: false, showOnlyForSystemAdmin: true, requiresAdminPermission: false },
    { id: 'plan-config' as AdminTab, label: t('admin.subscriptions'), icon: DollarSign, hideForSystemAdmin: false, showOnlyForSystemAdmin: true, requiresAdminPermission: false },
    { id: 'edm' as AdminTab, label: t('edm.title') || 'Email Marketing', icon: Mail, hideForSystemAdmin: false, showOnlyForSystemAdmin: true, requiresAdminPermission: false },
    { id: 'feature-flags' as AdminTab, label: 'Feature Flags', icon: Flag, hideForSystemAdmin: false, showOnlyForSystemAdmin: true, requiresAdminPermission: false },
    { id: 'branding' as AdminTab, label: 'Branding', icon: Palette, hideForSystemAdmin: false, showOnlyForSystemAdmin: true, requiresAdminPermission: false },
  ];

  const tabs = allTabs.filter(tab => {
    // System Administrator specific tabs
    if (tab.showOnlyForSystemAdmin && !isSystemAdmin) {
      return false;
    }
    
    // Hide certain tabs for System Administrators
    if (isSystemAdmin && tab.hideForSystemAdmin) {
      return false;
    }
    
    // Check Audit permission for audit tab
    if (tab.requiresAuditPermission && !hasPermission('Audit', 'view')) {
      return false;
    }
    
    // Check Admin permission for tabs that require it (unless user is System Administrator)
    if (!isSystemAdmin && tab.requiresAdminPermission && !hasAdminPermission) {
      return false;
    }
    
    return true;
  });

  const renderContent = () => {
    switch (activeTab) {
      case 'settings':
        return <AdminSettings />;
      case 'account':
        return <AdminAccount />;
      case 'notifications':
        return <AdminNotifications />;
      case 'accounts':
        return <AccountsManagement mode="page" />;
      case 'users':
        return <UserManagement mode="page" />;
      case 'roles':
        return <RoleManagement />;
      case 'audit':
        return <AuditLogs />;
      case 'extractor':
        return <ExtractorManager />;
      case 'webhooks':
        return <WebhookLogs />;
      case 'plan-config':
        return <SubscriptionPlanConfig />;
      case 'edm':
        return <EDMManager />;
      case 'feature-flags':
        return <FeatureFlags />;
      case 'branding':
        return <BrandingConfig />;
      default:
        return <AdminSettings />;
    }
  };

  // For System Administrators: Show content directly without tabs (navigation via main sidebar)
  if (isSystemAdmin) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        {renderContent()}
      </div>
    );
  }

  // For non-System Administrators: Use tab layout
  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('navigation.admin')}</h1>
        <p className="text-gray-600 dark:text-gray-400">{t('admin.description')}</p>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex -mb-px overflow-x-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  style={{ outline: 'none', boxShadow: 'none' }}
                  className={`flex items-center space-x-2 px-6 py-4 border-b-2 font-medium text-sm whitespace-nowrap transition-colors [&:focus]:outline-none [&:focus]:ring-0 [&:focus]:shadow-none [&:active]:outline-none ${
                    activeTab === tab.id
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};
